import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// TypeScript interfaces for Appeals
export interface AppealMessage {
  message: string;
  senderType: "admin" | "restaurant";
  senderId?: string;
  _id: string;
  attachments: {
    filename: string;
    url: string;
    _id: string;
  }[];
  createdAt: string;
}

export interface AppealLog {
  action: string;
  actor: string;
  details?: {
    reason?: string;
    note?: string;
  };
  _id: string;
  createdAt: string;
}

export interface Appeal {
  _id: string;
  appealId: string;
  restaurantId: {
    _id: string;
    businessInformation: {
      businessName: string;
    };
  };
  reason: string;
  status: "open" | "closed";
  messages: AppealMessage[];
  logs: AppealLog[];
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface CloseAppealResponse {
  success: boolean;
  message: string;
  data: Appeal;
}

export interface ReplyToAppealPayload {
  appealId: string;
  message: string;
  restaurantEmail: string;
}

export interface ReplyToAppealResponse {
  success: boolean;
  message: string;
  appeal: Appeal;
}

// Redux state interface
interface RestaurantAppealsState {
  appeals: Appeal[];
  currentAppeal: Appeal | null;
  status: "idle" | "loading" | "success" | "failed";
  closeStatus: "idle" | "loading" | "success" | "failed";
  replyStatus: "idle" | "loading" | "success" | "failed";
  detailsStatus: "idle" | "loading" | "success" | "failed";
  error: string | null;
  total: number;
}

const initialState: RestaurantAppealsState = {
  appeals: [],
  currentAppeal: null,
  status: "idle",
  closeStatus: "idle",
  replyStatus: "idle",
  detailsStatus: "idle",
  error: null,
  total: 0,
};

// Async thunks for API calls

// Get All Appeals
export const getAllAppeals = createAsyncThunk<
  Appeal[],
  void,
  { rejectValue: string }
>("restaurantAppeals/getAllAppeals", async (_, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    if (!token) {
      return rejectWithValue("Authentication token not found");
    }

    const response = await axios.get<Appeal[]>(
      `${process.env.REACT_APP_RESTAURANT_URL}/appeals`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch appeals"
    );
  }
});

// Close Appeal
export const closeAppeal = createAsyncThunk<
  CloseAppealResponse,
  string,
  { rejectValue: string }
>("restaurantAppeals/closeAppeal", async (appealId, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    if (!token) {
      return rejectWithValue("Authentication token not found");
    }

    const response = await axios.put<CloseAppealResponse>(
      `${process.env.REACT_APP_RESTAURANT_URL}/appeal/${appealId}/close`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to close appeal"
    );
  }
});

// Reply to Appeal
export const replyToAppeal = createAsyncThunk<
  ReplyToAppealResponse,
  ReplyToAppealPayload,
  { rejectValue: string }
>("restaurantAppeals/replyToAppeal", async (payload, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    if (!token) {
      return rejectWithValue("Authentication token not found");
    }

    const response = await axios.post<ReplyToAppealResponse>(
      `${process.env.REACT_APP_RESTAURANT_URL}/appeal/reply`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to send reply"
    );
  }
});

// Redux slice
const restaurantAppealsSlice = createSlice({
  name: "restaurantAppeals",
  initialState,
  reducers: {
    clearCurrentAppeal: (state) => {
      state.currentAppeal = null;
      state.detailsStatus = "idle";
    },
    clearErrors: (state) => {
      state.error = null;
    },
    resetCloseStatus: (state) => {
      state.closeStatus = "idle";
    },
    resetReplyStatus: (state) => {
      state.replyStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Appeals
      .addCase(getAllAppeals.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllAppeals.fulfilled, (state, action) => {
        state.status = "success";
        state.appeals = action.payload;
        state.total = action.payload.length;
        state.error = null;
      })
      .addCase(getAllAppeals.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch appeals";
      })

      // Close Appeal
      .addCase(closeAppeal.pending, (state) => {
        state.closeStatus = "loading";
        state.error = null;
      })
      .addCase(closeAppeal.fulfilled, (state, action) => {
        state.closeStatus = "success";
        // Update the appeal in the list
        const index = state.appeals.findIndex(
          (appeal) => appeal._id === action.payload.data._id
        );
        if (index !== -1) {
          state.appeals[index] = action.payload.data;
        }
        // Update current appeal if it matches
        if (state.currentAppeal?._id === action.payload.data._id) {
          state.currentAppeal = action.payload.data;
        }
        state.error = null;
      })
      .addCase(closeAppeal.rejected, (state, action) => {
        state.closeStatus = "failed";
        state.error = action.payload || "Failed to close appeal";
      })

      // Reply to Appeal
      .addCase(replyToAppeal.pending, (state) => {
        state.replyStatus = "loading";
        state.error = null;
      })
      .addCase(replyToAppeal.fulfilled, (state, action) => {
        state.replyStatus = "success";
        // Update the appeal in the list
        const index = state.appeals.findIndex(
          (appeal) => appeal._id === action.payload.appeal._id
        );
        if (index !== -1) {
          state.appeals[index] = action.payload.appeal;
        }
        // Update current appeal if it matches
        if (state.currentAppeal?._id === action.payload.appeal._id) {
          state.currentAppeal = action.payload.appeal;
        }
        state.error = null;
      })
      .addCase(replyToAppeal.rejected, (state, action) => {
        state.replyStatus = "failed";
        state.error = action.payload || "Failed to send reply";
      });
  },
});

export const { clearCurrentAppeal, clearErrors, resetCloseStatus, resetReplyStatus } =
  restaurantAppealsSlice.actions;
export default restaurantAppealsSlice.reducer;
